"use client";

import Image from "next/image";
import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import type { Lang } from "@/lib/lang";
import { COPY } from "./copy";
import { Overline, PlayIcon, Reveal, usePrefersReducedMotion } from "./ui";

/* ── Coaches: Matchday on the touchline ──────────────────────────────────── */

function EventGlyph({ kind }: { kind: string }) {
  if (kind === "card") return <span className="block h-[14px] w-[10px] rounded-[2px]" style={{ background: "#F5C542" }} />;
  if (kind === "sub")
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M7 4v13M3.5 13.5 7 17l3.5-3.5" style={{ stroke: "var(--sn-good)" }} /><path d="M17 20V7M13.5 10.5 17 7l3.5 3.5" style={{ stroke: "var(--sn-bad)" }} />
      </svg>
    );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m12 7 4 3-1.5 4.5h-5L8 10z" fill="currentColor" />
    </svg>
  );
}

/** A phone running the app's Matchday screen, built in code like the app is. */
function MatchdayPhone({ lang }: { lang: Lang }) {
  const k = COPY.coaches;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "0px 0px -20% 0px" });
  const reduced = usePrefersReducedMotion();
  const [secs, setSecs] = useState(63 * 60 + 12);
  useEffect(() => {
    if (!inView || reduced) return;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [inView, reduced]);
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");

  return (
    <div ref={ref} className="relative mx-auto w-[300px] rounded-[48px] p-[10px] sm:w-[320px]" style={{ background: "#0b0d14", boxShadow: "0 40px 120px -30px rgba(0,0,0,.9), inset 0 0 0 1.5px rgba(255,255,255,.08)" }}>
      <div className="relative overflow-hidden rounded-[38px]" style={{ background: "var(--sn-bg)", height: 620 }}>
        <div className="absolute left-1/2 top-2.5 h-[26px] w-[92px] -translate-x-1/2 rounded-full" style={{ background: "#000" }} />
        <div className="px-5 pt-14">
          <p className="sn-over !text-[10px]">{k.phoneOver[lang]}</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="sn-rec h-2 w-2 rounded-full" style={{ background: "var(--sn-good)" }} />
            <span className="sn-num text-[13px]" style={{ color: "var(--sn-good)" }}>{mm}&apos;</span>
          </div>
          <div className="mt-2 grid grid-cols-[1fr_auto] items-baseline gap-x-3">
            <span className="sn-wide text-[17px]">Academia Norte</span>
            <span className="sn-num text-[30px] leading-none">2</span>
            <span className="sn-wide text-[17px] opacity-45">Atlético Sur</span>
            <span className="sn-num text-[30px] leading-none opacity-45">1</span>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-full px-3 py-2 text-[12px] font-semibold" style={{ background: "rgba(255,107,122,.12)", color: "#ffd5da" }}>
            <span className="sn-rec h-2 w-2 rounded-full" style={{ background: "var(--sn-bad)" }} />
            {k.camRec[lang]} · <span className="sn-num">{mm}:{ss}</span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <span className="flex h-11 items-center justify-center rounded-full text-[13px] font-bold" style={{ background: "var(--sn-gold)", color: "var(--sn-gold-ink)" }}>{k.goal[lang]}</span>
            <span className="flex h-11 items-center justify-center rounded-full text-[13px] font-semibold" style={{ background: "var(--sn-glass-strong)" }}>{k.card[lang]}</span>
            <span className="flex h-11 items-center justify-center rounded-full text-[13px] font-semibold" style={{ background: "var(--sn-glass-strong)" }}>{k.sub[lang]}</span>
          </div>
          <p className="mt-6 text-[15px] font-bold">{k.feed[lang]}</p>
          <ul className="mt-2">
            {k.events.map((e, i) => (
              <li key={e.min} className="flex items-center gap-3 border-b py-3" style={{ borderColor: "var(--sn-hair)", opacity: e.kind === "goal-opp" ? 0.6 : 1 }}>
                <span className="sn-num w-8 text-[12.5px]" style={{ color: "var(--sn-fg2)" }}>{e.min}</span>
                <span className="flex w-5 justify-center"><EventGlyph kind={e.kind} /></span>
                <span className="text-[13.5px] font-medium">{e.text[lang]}</span>
                {i === 0 && <span className="ml-auto h-1.5 w-1.5 rounded-full" style={{ background: "var(--sn-gold)" }} />}
              </li>
            ))}
          </ul>
        </div>
        <div className="absolute inset-x-3 bottom-3 grid h-14 grid-cols-5 items-center rounded-full" style={{ background: "rgba(28,32,44,.74)", border: "1px solid rgba(255,255,255,.1)", backdropFilter: "blur(18px)" }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="mx-auto h-1.5 w-6 rounded-full" style={{ background: i === 2 ? "var(--sn-gold)" : "rgba(255,255,255,.28)" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function Coaches({ lang }: { lang: Lang }) {
  const k = COPY.coaches;
  return (
    <section id="coaches" className="relative scroll-mt-16 overflow-hidden py-24 sm:py-32 lg:py-40">
      <div className="mx-auto grid max-w-[1360px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
        <div className="lg:order-1">
          <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
          <Reveal delay={0.06}><h2 className="sn-wide mt-4 text-[clamp(32px,4.6vw,66px)]">{k.title[lang]}</h2></Reveal>
          <Reveal delay={0.12}><p className="sn-body mt-6">{k.body[lang]}</p></Reveal>
          <Reveal delay={0.18}>
            <ul className="mt-8 flex flex-wrap gap-2">
              {k.chips.map((chip) => (
                <li key={chip.en} className="rounded-full px-4 py-2 text-[13.5px] font-semibold" style={{ background: "var(--sn-glass)", border: "1px solid var(--sn-hair)" }}>
                  {chip[lang]}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal className="relative lg:order-2" y={40}>
          <div className="relative mx-auto h-[640px] max-w-[560px] sm:h-[700px]">
            <div className="absolute inset-y-6 left-0 w-[68%] overflow-hidden rounded-[32px] sm:inset-y-0">
              <Image src="/landing/coach.webp" alt={k.alt[lang]} fill sizes="(min-width:1024px) 30vw, 70vw" className="object-cover" style={{ objectPosition: "100% 22%" }} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,7,13,0) 55%, rgba(5,7,13,.55) 100%)" }} />
            </div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 scale-[.86] sm:scale-100">
              <MatchdayPhone lang={lang} />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── Players: moments ────────────────────────────────────────────────────── */

export function Players({ lang }: { lang: Lang }) {
  const k = COPY.players;
  return (
    <section id="players" className="relative scroll-mt-16 py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-[1.35fr_1fr] lg:items-end">
          <div>
            <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
            <Reveal delay={0.06}><h2 className="sn-wide mt-4 max-w-[22ch] text-[clamp(32px,4.2vw,62px)]">{k.title[lang]}</h2></Reveal>
          </div>
          <Reveal delay={0.12}><p className="sn-body lg:justify-self-end">{k.body[lang]}</p></Reveal>
        </div>

        <Reveal delay={0.1} className="mt-12 flex items-baseline justify-between gap-4">
          <p className="text-[18px] font-bold">{k.momentsTitle[lang]}</p>
          <p className="text-[13.5px] font-semibold" style={{ color: "var(--sn-gold)" }}>{k.summary[lang]}</p>
        </Reveal>
      </div>

      <div className="mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:px-8 lg:mx-auto lg:grid lg:max-w-[1360px] lg:grid-cols-3 lg:gap-6 lg:overflow-visible" style={{ scrollbarWidth: "none" }}>
        {k.moments.map((m, i) => (
          <Reveal key={m.min} delay={0.08 * i} className="w-[80%] shrink-0 snap-start sm:w-[46%] lg:w-auto">
            <div className="group relative aspect-[16/10] overflow-hidden rounded-[22px] lg:aspect-[4/5] lg:rounded-[28px]">
              <Image src={m.img} alt="" fill sizes="(min-width:1024px) 30vw, 80vw" className="object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]" style={{ objectPosition: m.pos }} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,.82))" }} />
              <span className="absolute left-4 top-4 h-1 w-8 rounded-full" style={{ background: m.good ? "var(--sn-good)" : "var(--sn-bad)" }} />
              <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-3 lg:inset-x-5 lg:bottom-5">
                <div>
                  <p className="sn-num text-[13px]" style={{ color: "var(--sn-fg2)" }}>{m.min}</p>
                  <p className="text-[17px] font-semibold lg:text-[19px]">{m.label[lang]}</p>
                </div>
                <span className="flex h-10 w-10 items-center justify-center rounded-full transition-colors group-hover:bg-white/25" style={{ background: "rgba(255,255,255,.16)", backdropFilter: "blur(10px)" }} aria-label={k.play[lang]} role="img">
                  <PlayIcon size={13} />
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mx-auto mt-10 max-w-[1360px] px-5 sm:px-8">
        <Reveal>
          <dl className="grid grid-cols-2 gap-y-6 sm:grid-cols-4">
            {k.stats.map((s, i) => (
              <div key={s.label.en} className={`flex flex-col gap-1.5 ${i % 2 ? "border-l pl-5" : ""} ${i === 2 ? "sm:border-l sm:pl-5" : ""}`} style={{ borderColor: "var(--sn-hair)" }}>
                <dd className="sn-num text-[clamp(28px,3.4vw,44px)] leading-none">{s.value}</dd>
                <dt className="text-[13px] font-medium" style={{ color: "var(--sn-fg2)" }}>{s.label[lang]}</dt>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}
